import React, { Suspense, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { useSelector } from 'react-redux';

import { RouteEnum } from 'app/shared/enums';
import lazyWithReload from 'app/shared/helpers/lazyWithReload';
import retryImport from 'app/shared/helpers/retryImport';
import { CalculationViewContentPlaceholder } from 'app/components/CalculationViewContentPlaceholder';
import { CalculationFirstStepPlaceholder } from 'app/components/CalculationFirstStepPlaceholder';
import { selectIsFirstStepModeView } from 'app/store/selectors/selectIsFirstStepModeView';
import { selectIsFirstStepModeUnknown } from 'app/store/selectors/selectIsFirstStepModeUnknown';
import { FallbackModal } from 'app/components/FallbackModal';
import { Loader } from 'app/components/Loader';
import { CalculationViewPageHeader } from 'app/components/CalculationViewPageHeader';
import QuestionsAndAnswersSection from 'app/components/QuestionsAndAnswersSection';
import { getQuotationId } from 'app/store/data/quotation';
import {
  isPrerendering,
  wasPrerendered,
} from 'app/shared/helpers/isPrerendering';
import { warmupHomepageConnection } from 'app/shared/helpers/warmupHomepageConnection';

import { CalculationBaseViewProps } from './CalculationBaseView.interfaces';
import { useCalculationBaseView } from './useCalculationBaseView';

const EnableCookiesModal = lazyWithReload(() =>
  retryImport(() => import('app/components/EnableCookiesModal'))
);

const CalculationSummaryView = lazyWithReload(() =>
  retryImport(() => import('app/views/CalculationSummaryView'))
);

const CheckoutBaseView = lazyWithReload(() =>
  retryImport(() => import('app/views/CheckoutView'))
);

const NotFoundPage = lazyWithReload(() =>
  retryImport(() => import('app/components/NotFoundPage'))
);

const CalculationView = lazyWithReload(() =>
  retryImport(() => import('app/views/CalculationView'))
);

const CalculationViewComponents = () => {
  const quotationIdFromStore = useSelector(getQuotationId);
  const isFirstStepModeView = useSelector(selectIsFirstStepModeView);
  const isFirstStepModeUnknown = useSelector(selectIsFirstStepModeUnknown);

  const placeholder = isFirstStepModeView ? (
    <CalculationFirstStepPlaceholder />
  ) : isFirstStepModeUnknown ? (
    <Loader />
  ) : (
    <CalculationViewContentPlaceholder showLoadingAnimation={false} />
  );

  return (
    <>
      <CalculationViewPageHeader />
      {quotationIdFromStore && !isPrerendering ? (
        <Suspense fallback={placeholder}>
          <CalculationView />
        </Suspense>
      ) : (
        <>
          {placeholder}
          <div id="seo-prerender" />
        </>
      )}
      {!isFirstStepModeUnknown && <QuestionsAndAnswersSection />}
    </>
  );
};

const CalculationBaseView = (props: CalculationBaseViewProps) => {
  const {
    match: { url },
    location,
  } = props;

  const quotationIdFromStore = useSelector(getQuotationId);

  const { disabledCookies, getShouldShowResults } = useCalculationBaseView(
    props
  );

  const isResultsOrCheckoutRoute =
    location.pathname.startsWith(`${url}/results`) ||
    location.pathname.startsWith(`${url}/checkout`);

  const Placeholder = () =>
    isResultsOrCheckoutRoute ? (
      <Loader />
    ) : (
      <CalculationViewContentPlaceholder />
    );

  useEffect(() => warmupHomepageConnection(), []);

  if (disabledCookies) {
    return (
      <Suspense fallback={<FallbackModal />}>
        <EnableCookiesModal />;
      </Suspense>
    );
  }

  if (!wasPrerendered && !quotationIdFromStore) {
    return <Loader />;
  }

  const isCalculationFormRoute =
    location.pathname.startsWith(`${url}/form`) ||
    location.pathname === '/' ||
    location.pathname === `${RouteEnum.HOME}` ||
    location.pathname === `${RouteEnum.HOME}/` ||
    location.pathname === `${RouteEnum.CALCULATION}` ||
    location.pathname === `${RouteEnum.CALCULATION}/` ||
    location.pathname.startsWith(`/promotion-id/`) ||
    location.pathname.startsWith(`${RouteEnum.HOME}/promotion-id/`) ||
    location.pathname.startsWith(`${RouteEnum.HOME}/partners/`);

  if (getShouldShowResults() && isCalculationFormRoute) {
    return (
      <Redirect
        to={{
          pathname: RouteEnum.CALCULATION_RESULTS,
          search: location.search,
          state: {
            fromCalculationForm: isCalculationFormRoute,
          },
        }}
      />
    );
  } else if (
    !getShouldShowResults() &&
    location.pathname.startsWith(`${url}/results`)
  ) {
    return (
      <Redirect
        to={{
          pathname: `${url}/form`,
          search: location.search,
        }}
      />
    );
  }

  return (
    <Suspense fallback={<Placeholder />}>
      <Switch location={location}>
        <Route path={url} exact component={CalculationViewComponents} />
        <Route path={`${url}/form`} component={CalculationViewComponents} />
        <Route path={`${url}/results`} component={CalculationSummaryView} />
        <Route path={`${url}/checkout/:offerId`} component={CheckoutBaseView} />
        <Route component={NotFoundPage} />
      </Switch>
    </Suspense>
  );
};

export default CalculationBaseView;
